import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Radio,
  RadioGroup,
  Select,
  Text,
} from '@chakra-ui/react'
import { WizardVehicleAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useContext, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Datepicker } from '../components/Datepicker'
import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { FormSpacer } from '../components/FormSpacer'
import { InputGroup } from '../components/InputGroup'
import { RadioStack } from '../components/RadioStack'
import { TextareaGroup } from '../components/TextareaGroup'
import { useSelections } from '../hooks'
import { useGetVehicleInspectionDistricts } from '../hooks/useWizard'
import { insuranceParamsContext } from '../layouts/Wizard'
import { Step4Context } from '../pages/carkulka/wizard/4'

export const AdditionalVehicleInfo = () => {
  const {
    register,
    formState: { errors },
    getValues,
    watch,
    control,
    setValue,
  } = useFormContext<WizardVehicleAdditionalData>()
  const { insuranceParams } = useContext(insuranceParamsContext)
  const { offer } = useContext(Step4Context)
  const isContract = !!insuranceParams?.generalParams.contractNumber

  const licensePlateTypes = getValues('vehicle.licensePlateTypes')
  const {
    data: dataPurchaseMethod,
    isLoading: isLoadingPurchaseMethod,
    isFetching: isFetchingPurchaseMethod,
  } = useSelections(SelectionType.PurchaseMethod)
  const { data: inspectionDistrictsData, refetch: refetchInspectionDistricts } =
    useGetVehicleInspectionDistricts(offer?.offerId?.toString(), {
      enabled: false,
    })
  useEffect(() => {
    if (offer?.offerId) {
      refetchInspectionDistricts()
    }
  }, [offer])

  const invoiceDate = getValues('vehicle.invoiceDate')
  const maxSpeedRequired = getValues('vehicle.isVehicleMaxSpeedRequired')
  const licensePlateTypeId = watch('vehicle.licensePlateTypeId')
  let rzAndTpDisabled = false
  if (licensePlateTypeId && licensePlateTypeId == Selection.PermanentlyUnallocated) {
    rzAndTpDisabled = true
    setValue('vehicle.licensePlate', '')
    setValue('vehicle.cisloTechnickehoPrukazu', '')
    setValue('vehicle.cisloOrv', '')
  }

  const inspectionDateRequired = watch('vehicle.isInspectionDateRequired')
  const inspectionDate = watch('vehicle.inspectionDate')
  // Register for required validation
  register('vehicle.inspectionDate', {
    required: inspectionDateRequired ? 'Zadejte datum prohlídky vozidla.' : false,
  })

  return (
    <>
      <FormGroup>
        <FormControl isInvalid={!!errors.vehicle?.vin}>
          <FormLabel tooltip="Zadejte identifikační číslo vozidla (VIN), které naleznete v technickém průkazu.">
            VIN
          </FormLabel>

          <InputGroup
            isDisabled={isContract}
            id="vehicle.vin"
            htmlSize={18}
            maxLength={17}
            errors={errors.vehicle?.vin}
            {...register('vehicle.vin', {
              onChange: (e) => {
                setValue('vehicle.vin', e.target.value.toUpperCase())
              },
            })}
          />
        </FormControl>

        <FormControl isInvalid={!!errors.vehicle?.cisloTechnickehoPrukazu}>
          <FormLabel>Číslo velkého TP</FormLabel>

          <InputGroup
            id="tp"
            htmlSize={10}
            placeholder="Číslo TP"
            {...register('vehicle.cisloTechnickehoPrukazu', {
              onChange: (e) => {
                setValue('vehicle.cisloTechnickehoPrukazu', e.target.value.toUpperCase())
              },
            })}
            isDisabled={isContract || rzAndTpDisabled}
            errors={errors.vehicle?.cisloTechnickehoPrukazu}
          />
        </FormControl>

        <Text variant="textMuted" sx={{ alignSelf: 'end', height: '30px' }}>
          nebo
        </Text>

        <FormControl isInvalid={!!errors.vehicle?.cisloOrv}>
          <FormLabel>Číslo ORV</FormLabel>

          <InputGroup
            id="tp"
            htmlSize={10}
            placeholder="Číslo ORV"
            {...register('vehicle.cisloOrv', {
              onChange: (e) => {
                setValue('vehicle.cisloOrv', e.target.value.toUpperCase())
              },
            })}
            isDisabled={isContract || rzAndTpDisabled}
            errors={errors.vehicle?.cisloOrv}
          />
        </FormControl>
      </FormGroup>

      <FormGroup>
        <FormControl>
          <FormLabel tooltip="Reg. značka (SPZ)">Reg. značka (SPZ)</FormLabel>

          <InputGroup
            id="spz"
            htmlSize={10}
            placeholder="SPZ"
            {...register('vehicle.licensePlate', {
              onChange: (e) => {
                setValue('vehicle.licensePlate', e.target.value.toUpperCase())
              },
            })}
            isDisabled={isContract || rzAndTpDisabled}
          />
        </FormControl>

        {getValues('vehicle.isLicencePlateTypeRequired') && (
          <FormControl isRequired>
            <FormLabel tooltip="Druh registrační značky (SPZ)">
              Druh registrační značky (SPZ)
            </FormLabel>

            <Select
              id="vehicle.licensePlateTypeId"
              {...register('vehicle.licensePlateTypeId')}
              isDisabled={isContract}
              value={watch('vehicle.licensePlateTypeId')?.toString() ?? ''}
            >
              <option disabled value="">
                Vyberte
              </option>
              {licensePlateTypes &&
                licensePlateTypes?.map((item) => (
                  <option value={item.id.toString()} key={item.id}>
                    {item.description}
                  </option>
                ))}
            </Select>
          </FormControl>
        )}

        {offer?.insurerId == Insurer.CPP && (
          <FormControl isInvalid={!!errors.vehicle?.purchaseMethod}>
            <FormLabel>Způsob pořízení vozdila</FormLabel>

            <Select
              id="vehicle.purchaseMethod"
              {...register('vehicle.purchaseMethod')}
              isDisabled={isContract}
              value={watch('vehicle.purchaseMethod') ?? ''}
            >
              <option disabled value="">
                Vyberte
              </option>
              {dataPurchaseMethod &&
                dataPurchaseMethod?.data.map((item) => (
                  <option value={item.value?.toString()} key={item.id}>
                    {item.description}
                  </option>
                ))}
            </Select>

            {errors.vehicle?.purchaseMethod && (
              <FormErrorMessage>{errors.vehicle.purchaseMethod.message}</FormErrorMessage>
            )}
          </FormControl>
        )}

        {[Insurer.CPP, Insurer.PVZP].includes(offer?.insurerId) && (
          <FormControl isInvalid={!!errors.vehicle?.firstOwner} isDisabled={isContract}>
            <FormLabel
              htmlFor="firstOwner"
              tooltip="Vyberte, zda je současný majitel prvním majitelem vozidla."
            >
              První majitel
            </FormLabel>

            <Controller
              name="vehicle.firstOwner"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onChange={(e) => onChange(e == '1')}
                  value={value == true ? '1' : value === false ? '0' : undefined}
                >
                  <RadioStack variant="stackedTile">
                    <Radio value={'1'}>Ano</Radio>
                    <Radio value={'0'}>Ne</Radio>
                  </RadioStack>
                </RadioGroup>
              )}
            />

            {errors.vehicle?.firstOwner && (
              <FormErrorMessage>{errors.vehicle.firstOwner.message}</FormErrorMessage>
            )}
          </FormControl>
        )}
      </FormGroup>

      <FormControl variant="fullWidth" isInvalid={!!errors.vehicle?.typeDescription}>
        <FormLabel>Bližší specifikace vozidla</FormLabel>

        <InputGroup
          isDisabled={isContract}
          placeholder="Např. detailní model, motorizace apod."
          errors={errors.vehicle?.typeDescription}
          {...register('vehicle.typeDescription')}
        />
      </FormControl>

      {maxSpeedRequired && (
        <FormControl isInvalid={!!errors.vehicle?.vehicleMaxSpeed} isRequired>
          <FormLabel>Max. konstrukční rychlost</FormLabel>

          <InputGroup
            isDisabled={isContract}
            id="vehicle.vehicleMaxSpeed"
            placeholder="Např. 25"
            htmlSize={6}
            autoComplete="off"
            inputMode="decimal"
            errors={errors.vehicle?.vehicleMaxSpeed}
            rightElement="km/h"
            {...register('vehicle.vehicleMaxSpeed')}
          />
        </FormControl>
      )}

      {inspectionDateRequired && (
        <FormControl
          isInvalid={!!errors.vehicle?.inspectionDate}
          isRequired={inspectionDateRequired}
        >
          <FormLabel htmlFor="vehicle.inspectionDate">Datum prohlídky vozidla</FormLabel>
          <Datepicker
            isDisabled={isContract}
            errors={errors.vehicle?.inspectionDate}
            date={inspectionDate}
            onDateChange={(date) =>
              setValue('vehicle.inspectionDate', date, { shouldValidate: true })
            }
          />
        </FormControl>
      )}

      {getValues('vehicle.isInspectionDistrictRequired') && inspectionDistrictsData?.data && (
        <FormControl>
          <FormLabel htmlFor="vehicle.inspectionDistrict">Místo prohlídky</FormLabel>

          <Select
            id="vehicle.inspectionDistrict"
            {...register('vehicle.inspectionDistrict')}
            isDisabled={isContract}
          >
            <option disabled value="">
              Vyberte
            </option>
            {inspectionDistrictsData?.data &&
              inspectionDistrictsData?.data.map((item) => (
                <option value={item.value!.toString()} key={item.value}>
                  {item.name}
                </option>
              ))}
          </Select>
        </FormControl>
      )}

      {getValues('vehicle.isPreferredInspectionTypeRequired') &&
        offer?.insurerId == Insurer.Kooperativa && (
          <FormControl isInvalid={!!errors.vehicle?.preferredInspectionType}>
            <FormLabel>Preferovaný typ prohlídky</FormLabel>

            <Select
              id="vehicle.preferredInspectionType"
              {...register('vehicle.preferredInspectionType')}
              isDisabled={isContract}
              defaultValue={getValues('vehicle.preferredInspectionType') ?? ''}
            >
              <option value="P">fotodokumentace vozidla pojistníkem</option>
              <option value="I">prohlídka technikem Global Expert</option>
            </Select>

            {errors.vehicle?.preferredInspectionType && (
              <FormErrorMessage>{errors.vehicle.preferredInspectionType.message}</FormErrorMessage>
            )}
          </FormControl>
        )}

      <FormSpacer />

      <Box
        sx={{
          w: '100%',
          display: 'grid',
          gridTemplateColumns: { base: '1fr', md: 'auto 1fr' },
          placeItems: 'start',
          gap: 8,
        }}
      >
        <FormControl>
          <FormLabel>Poškození vozidla</FormLabel>

          <CheckboxGroup isDisabled={isContract}>
            <Checkbox {...register('vehicle.isDamaged')}>Vůz je v době sjednání poškozen</Checkbox>
            <Checkbox {...register('vehicle.hadSevereDamageInPast')}>
              {' '}
              Vůz byl v minulosti vážně poškozen
            </Checkbox>
          </CheckboxGroup>
        </FormControl>

        <FormControl variant="fullWidth">
          <FormLabel tooltip="Popis poškození">Popis poškození</FormLabel>

          <TextareaGroup
            isDisabled={isContract}
            placeholder="Např. Oděrka na pravém předním blatníku..."
            {...register('vehicle.damageDescription')}
          />

          <FormHelperText>Tento popis bude uveden ve smlouvě.</FormHelperText>
        </FormControl>
      </Box>

      {getValues('vehicle.isInvoiceInfoRequired') && (
        <>
          <FormSpacer />

          <FormGroup>
            <FormControl isInvalid={!!errors.vehicle?.invoiceNumber} isRequired>
              <FormLabel tooltip="UNIQA vyžaduje doplnění údajů o faktuře, pokud byla z ní určena cena vozidla.">
                Číslo faktury
              </FormLabel>

              <InputGroup
                id="invoiceNumber"
                htmlSize={10}
                {...register('vehicle.invoiceNumber')}
                isDisabled={isContract}
                errors={errors.vehicle?.invoiceNumber}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.vehicle?.invoiceDate} isRequired>
              <FormLabel htmlFor="vehicle.invoiceDate">Datum vystavení faktury</FormLabel>
              <Datepicker
                isDisabled={isContract}
                errors={errors.vehicle?.invoiceDate}
                date={invoiceDate}
                onDateChange={(date) => setValue('vehicle.invoiceDate', date)}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.vehicle?.invoiceSelerIco} isRequired>
              <FormLabel>IČO prodejce</FormLabel>

              <InputGroup
                id="invoiceSelerIco"
                htmlSize={10}
                {...register('vehicle.invoiceSelerIco')}
                isDisabled={isContract}
                errors={errors.vehicle?.invoiceSelerIco}
              />
            </FormControl>
          </FormGroup>
        </>
      )}
    </>
  )
}
//
